import { useEffect, useRef, useState } from 'react'
import { intl } from '../../../../../i18n'
import sprint, {
  SprintDetail,
  SprintStatus,
  SprintStatusDisplayOrder,
} from '../../../../../lib/functions/sprint'
import { SelectOption } from '../../../../model/selectOption'
import { CheckboxSelectConditionItemLabel } from '../CheckboxSelectConditionItemLabel'

const getSortIndex = (status: SprintStatus) =>
  SprintStatusDisplayOrder.indexOf(status)

type SprintMultiAutocompleteConditionItemLabelProps = {
  value: string[] | undefined
  teamUuids: string[] | undefined
  isLoading?: boolean
  onChange: (value: string[] | undefined) => void
  search: () => void
  onClose?: () => void
  sprintOptions?: SelectOption[]
}

export const SprintMultiAutocompleteConditionItemLabel = ({
  value,
  teamUuids,
  isLoading,
  onChange,
  onClose,
  search,
  sprintOptions: externalSprintOptions,
}: SprintMultiAutocompleteConditionItemLabelProps) => {
  const [internalSprintOptions, setInternalSprintOptions] = useState<
    SelectOption[]
  >([])
  const sprintCacheRef = useRef<Map<string, SprintDetail[]>>(new Map())

  useEffect(() => {
    if (externalSprintOptions) return
    if (!teamUuids || teamUuids.length === 0) {
      sprintCacheRef.current.clear()
      setInternalSprintOptions([])
      return
    }

    const cache = sprintCacheRef.current
    const currentUuids = new Set(teamUuids)

    // 選択解除されたチームのキャッシュを削除
    for (const key of cache.keys()) {
      if (!currentUuids.has(key)) {
        cache.delete(key)
      }
    }

    // 未取得のチームだけ特定
    const newUuids = teamUuids.filter(uuid => !cache.has(uuid))

    const buildOptions = () =>
      Array.from(cache.values())
        .flat()
        .sort((a, b) => getSortIndex(a.status) - getSortIndex(b.status))
        .map(v => ({ value: v.uuid, name: v.name, groupBy: v.status }))

    if (newUuids.length === 0) {
      // キャッシュのみで再構成（チーム削除時）
      setInternalSprintOptions(buildOptions())
      return
    }

    // 新規チームのみフェッチ
    Promise.all(
      newUuids.map(async uuid => {
        const sprints = await sprint.searchByText(uuid)
        cache.set(uuid, sprints)
      })
    ).then(() => {
      setInternalSprintOptions(buildOptions())
    })
  }, [teamUuids, externalSprintOptions])

  const sprintOptions = externalSprintOptions ?? internalSprintOptions

  return (
    <CheckboxSelectConditionItemLabel
      header={intl.formatMessage({
        id: 'wbsItemSearchFilterPanel.sprint',
      })}
      value={value}
      onChange={onChange}
      onClose={onClose}
      search={search}
      options={sprintOptions}
      isLoading={isLoading}
    />
  )
}
